
import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useUser } from '@/contexts/UserContext';
import { OpsEndpointCard } from '@/components/ops/OpsEndpointCard';
import { TimestampDisplay } from '@/components/ops/TimestampDisplay';
import { EmptyState } from '@/components/ops/EmptyState';
import { LoadingSpinner } from '@/components/ops/LoadingSpinner';
import { HealthIndicator } from '@/components/ops/HealthIndicator';
import { OpsErrorBoundary } from '@/components/ops/OpsErrorBoundary';
import { Activity } from 'lucide-react';

const API_BASE = import.meta.env.VITE_RAG_API_URL;

interface HeartbeatData {
    alive: boolean;
    last_query_at: number | null;
    last_query_ago_seconds: number | null;
    queries_total: number;
    queries_last_5min: number;
    timestamp: number;
}

export function HeartbeatPage() {
    const { session } = useUser();
    const [data, setData] = useState<HeartbeatData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        if (session?.access_token) {
            fetchData();
        } else {
            setLoading(false);
            setError("Authentication required");
        }
        return () => {
            isMounted.current = false;
        };
    }, [session]);

    const fetchData = async () => {
        if (!session?.access_token) return;

        try {
            setLoading(true);
            setError(null);
            const res = await axios.get(`${API_BASE}/ops/retrieval/heartbeat`, {
                headers: { Authorization: `Bearer ${session.access_token}` },
                timeout: 5000
            });

            if (isMounted.current) {
                setData(res.data);
            }
        } catch (err: any) {
            if (isMounted.current) {
                if (err.response?.status === 401) {
                    setError("Unauthorized");
                } else {
                    setError(err.message || "Failed to fetch heartbeat");
                }
            }
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    if (!session) return <EmptyState title="Authentication Required" description="Please sign in to view system heartbeat." />;

    const getStatus = (d: HeartbeatData) => {
        if (!d.alive) return 'down';
        return 'healthy';
    };

    return (
        <OpsErrorBoundary>
            <div className="h-[calc(100vh-12rem)] max-w-4xl mx-auto p-4">
                <OpsEndpointCard
                    title="System Heartbeat"
                    endpoint="/ops/retrieval/heartbeat"
                    lastUpdated={data?.timestamp}
                    onRefresh={fetchData}
                    isLoading={loading}
                    error={error}
                >
                    {loading ? (
                        <LoadingSpinner fullHeight text="Checking system vital signs..." />
                    ) : data ? (
                        <div className="flex flex-col gap-8">

                            <div className="flex items-center justify-between bg-muted/10 p-6 rounded-lg border">
                                <div className="flex items-center gap-4">
                                    <div className="p-3 bg-muted rounded-full">
                                        <Activity className="h-6 w-6 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-medium">System Status</h3>
                                        <p className="text-sm text-muted-foreground">Real-time availability check</p>
                                    </div>
                                </div>
                                <HealthIndicator status={getStatus(data)} />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="p-6 border rounded-lg bg-card">
                                    <span className="text-sm text-muted-foreground">Total Queries Processed</span>
                                    <div className="text-4xl font-bold mt-2">{data.queries_total}</div>
                                </div>
                                <div className="p-6 border rounded-lg bg-card">
                                    <span className="text-sm text-muted-foreground">Queries (Last 5 min)</span>
                                    <div className="text-4xl font-bold mt-2">{data.queries_last_5min}</div>
                                </div>
                            </div>

                            <div className="p-6 border rounded-lg bg-card flex flex-col gap-2">
                                <h3 className="text-sm font-medium text-muted-foreground">Last Activity</h3>
                                {data.last_query_at ? (
                                    <div className="flex gap-4 items-baseline">
                                        <TimestampDisplay timestamp={data.last_query_at} label="Processed at" />
                                        {data.last_query_ago_seconds !== null && (
                                            <span className="text-xs text-muted-foreground font-mono">
                                                ({data.last_query_ago_seconds.toFixed(1)}s ago)
                                            </span>
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-muted-foreground italic">No queries recorded yet</div>
                                )}
                            </div>

                        </div>
                    ) : (
                        <EmptyState title="No Heartbeat" description="Unable to connect to observability service." />
                    )}
                </OpsEndpointCard>
            </div>
        </OpsErrorBoundary>
    );
}
